import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    InputAdornment,
    Link,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import CustomPagination from '../../components/Pagination';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { isMasterUser, patchStoredUser, readStoredUser } from '../../helpers/authSession';
import { applyEmail, applyName } from '../../helpers/captureMode';

const emptyForm = {
    title: '',
    body: '',
    link: '',
    audience: 'all', // all | plans | user
    target_user_uuid: '',
    target_plan_names: [],
    notify_immediately: true,
};

function formatWhen(value) {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '—';
    }
    return date.toLocaleString('pt-BR');
}

function audienceLabel(item) {
    if (item?.audience === 'all' || item?.broadcast) {
        return 'Todos';
    }
    if (item?.audience === 'plans' || (Array.isArray(item?.target_plan_names) && item.target_plan_names.length > 0)) {
        return `Planos: ${(item.target_plan_names || []).join(', ')}`;
    }
    const name = item?.target_user_name || '';
    const email = item?.target_user_email || '';
    if (name || email) {
        return [name, email].filter(Boolean).join(' — ');
    }
    return item?.target_user_uuid || 'Usuário';
}

const CreateNotificationPage = () => {
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checkingAccess, setCheckingAccess] = useState(!isMasterUser());
    const [users, setUsers] = useState([]);
    const [plans, setPlans] = useState([]);
    const [formData, setFormData] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);

    useEffect(() => {
        let cancelled = false;

        async function ensureMaster() {
            try {
                const me = await api.get('/auth/me');
                const user = me?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    if (!cancelled) {
                        setIsMaster(user.is_master);
                    }
                }
            } catch {
                if (!cancelled) {
                    setIsMaster(false);
                }
            } finally {
                if (!cancelled) {
                    setCheckingAccess(false);
                }
            }
        }

        ensureMaster();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isMaster) {
            return undefined;
        }
        let cancelled = false;
        Promise.all([
            api.get('/users'),
            api.get('/subscription-plans'),
        ])
            .then(([usersResponse, plansResponse]) => {
                if (cancelled) {
                    return;
                }
                const userList = usersResponse?.data?.data ?? [];
                const planList = plansResponse?.data?.data ?? [];
                setUsers(Array.isArray(userList) ? userList : []);
                setPlans(Array.isArray(planList) ? planList : []);
            })
            .catch(() => {
                if (!cancelled) {
                    setUsers([]);
                    setPlans([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [isMaster]);

    const loadHistory = useCallback(() => {
        if (!isMaster) {
            return;
        }
        setHistoryLoading(true);
        const params = {
            page: currentPage,
            limit: itemsPerPage,
        };
        const q = String(appliedSearch || '').trim();
        if (q) {
            params.q = q;
        }
        api.get('/notifications/sent', { params })
            .then((response) => {
                const data = response?.data?.data || {};
                setHistory(Array.isArray(data.notifications) ? data.notifications : []);
                setTotalPages(Math.max(1, Number(data.total_pages) || 1));
            })
            .catch(() => {
                setHistory([]);
                setTotalPages(1);
            })
            .finally(() => {
                setHistoryLoading(false);
            });
    }, [isMaster, currentPage, itemsPerPage, appliedSearch]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const handlePlanToggle = (planName) => {
        setFormData((prev) => {
            const selected = new Set(prev.target_plan_names);
            if (selected.has(planName)) {
                selected.delete(planName);
            } else {
                selected.add(planName);
            }
            return { ...prev, target_plan_names: Array.from(selected) };
        });
        if (errors.target_plan_names) {
            setErrors((prev) => ({ ...prev, target_plan_names: '' }));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setErrors({});
        try {
            if (formData.audience === 'plans' && formData.target_plan_names.length === 0) {
                setErrors({ target_plan_names: 'Selecione pelo menos um plano.' });
                return;
            }
            if (formData.audience === 'user' && !formData.target_user_uuid) {
                setErrors({ target_user_uuid: 'Selecione o destinatário.' });
                return;
            }

            const payload = {
                title: formData.title.trim(),
                body: formData.body.trim(),
                link: formData.link.trim() || undefined,
                broadcast: formData.audience === 'all',
                target_user_uuid: formData.audience === 'user' ? formData.target_user_uuid : undefined,
                target_plan_names: formData.audience === 'plans' ? formData.target_plan_names : undefined,
                notify_immediately: Boolean(formData.notify_immediately),
            };
            const response = await api.post('/notifications', payload);
            if (response?.data?.success === false) {
                setErrors(response.data.errors || {});
                return;
            }

            let successText = 'Aviso enviado.';
            if (formData.audience === 'all') {
                successText = 'Aviso enviado para todos os usuários.';
            } else if (formData.audience === 'plans') {
                successText = `Aviso enviado para os planos: ${formData.target_plan_names.join(', ')}.`;
            } else {
                successText = 'Aviso enviado para o usuário selecionado.';
            }

            await Swal.fire({
                icon: 'success',
                title: 'Notificação enviada',
                text: successText,
                timer: 2200,
                showConfirmButton: false,
            });
            setFormData(emptyForm);
            if (currentPage !== 1) {
                setCurrentPage(1);
            } else {
                loadHistory();
            }
        } catch (error) {
            const apiErrors = error?.response?.data?.errors;
            if (apiErrors) {
                setErrors(apiErrors);
            } else {
                await Swal.fire({
                    icon: 'error',
                    title: 'Falha ao enviar',
                    text: error?.response?.data?.error || error?.response?.data?.message || 'Tente novamente.',
                });
            }
        } finally {
            setLoading(false);
        }
    };

    if (checkingAccess) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }

    const masterLabel = readStoredUser()?.email || 'master';

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Notificações</Typography>
            </Breadcrumbs>

            <Typography variant="h5" gutterBottom>
                Enviar notificação
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Broadcast, por planos (ex.: Free + Growing) ou 1:1. Sessão: {masterLabel}
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 640 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    O destinatário verá o aviso no sino do header, com badge de não lidas.
                    Com &quot;Notificar imediatamente&quot;, quem estiver online também recebe um toast em tempo real.
                </Alert>

                <TextField
                    label="Título"
                    fullWidth
                    required
                    margin="normal"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    error={Boolean(errors.title)}
                    helperText={errors.title}
                    inputProps={{ maxLength: 200 }}
                />

                <TextField
                    label="Mensagem"
                    fullWidth
                    required
                    multiline
                    minRows={4}
                    margin="normal"
                    value={formData.body}
                    onChange={(e) => handleChange('body', e.target.value)}
                    error={Boolean(errors.body)}
                    helperText={errors.body}
                />

                <TextField
                    label="Link (opcional)"
                    fullWidth
                    margin="normal"
                    placeholder="/admin/orders ou https://..."
                    value={formData.link}
                    onChange={(e) => handleChange('link', e.target.value)}
                    error={Boolean(errors.link)}
                    helperText={errors.link || 'Caminho interno (/admin/...) ou URL externa.'}
                />

                <FormControl component="fieldset" sx={{ mt: 2, mb: 1 }} error={Boolean(errors.target_user_uuid || errors.target_plan_names)}>
                    <FormLabel component="legend">Audiência</FormLabel>
                    <RadioGroup
                        value={formData.audience}
                        onChange={(e) => handleChange('audience', e.target.value)}
                    >
                        <FormControlLabel value="all" control={<Radio />} label="Todos os usuários (broadcast)" />
                        <FormControlLabel value="plans" control={<Radio />} label="Por plano de assinatura" />
                        <FormControlLabel value="user" control={<Radio />} label="Usuário específico (1:1)" />
                    </RadioGroup>
                </FormControl>

                {formData.audience === 'plans' && (
                    <FormControl
                        component="fieldset"
                        sx={{ mt: 1, mb: 1, width: '100%' }}
                        error={Boolean(errors.target_plan_names)}
                    >
                        <FormLabel component="legend">Planos</FormLabel>
                        <FormGroup>
                            {plans.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    Nenhum plano cadastrado.
                                </Typography>
                            ) : (
                                plans.map((plan) => (
                                    <FormControlLabel
                                        key={plan.uuid || plan.name}
                                        control={
                                            <Checkbox
                                                checked={formData.target_plan_names.includes(plan.name)}
                                                onChange={() => handlePlanToggle(plan.name)}
                                            />
                                        }
                                        label={plan.name}
                                    />
                                ))
                            )}
                        </FormGroup>
                        <FormHelperText>
                            {errors.target_plan_names || 'Selecione um ou mais planos (ex.: Free e Growing).'}
                        </FormHelperText>
                    </FormControl>
                )}

                {formData.audience === 'user' && (
                    <TextField
                        select
                        label="Destinatário"
                        fullWidth
                        margin="normal"
                        required
                        value={formData.target_user_uuid}
                        onChange={(e) => handleChange('target_user_uuid', e.target.value)}
                        error={Boolean(errors.target_user_uuid)}
                        helperText={errors.target_user_uuid}
                    >
                        <MenuItem value="">
                            <em>Selecione</em>
                        </MenuItem>
                        {users.map((user) => (
                            <MenuItem key={user.uuid || user.id} value={user.uuid || user.id}>
                                {(applyName(user.name) || 'Sem nome') + ' — ' + (applyEmail(user.email) || '')}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                <FormControlLabel
                    sx={{ mt: 2, alignItems: 'flex-start' }}
                    control={
                        <Checkbox
                            checked={Boolean(formData.notify_immediately)}
                            onChange={(e) => handleChange('notify_immediately', e.target.checked)}
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body2">Notificar imediatamente</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Envia um alerta em tempo real para quem estiver com o dashboard aberto.
                            </Typography>
                        </Box>
                    }
                />

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button type="submit" variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={22} color="inherit" /> : 'Enviar'}
                    </Button>
                </Box>
            </Box>

            <Box sx={{ mt: 6 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={2}>
                    <Box>
                        <Typography variant="h6">Histórico de envios</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Notificações já enviadas pelo sistema.
                        </Typography>
                    </Box>
                    <TextField
                        select
                        size="small"
                        label="Por página"
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value) || DEFAULT_LIST_PAGE_SIZE);
                            setCurrentPage(1);
                        }}
                        sx={{ minWidth: 120 }}
                    >
                        {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>

                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder="Buscar por título ou conteúdo"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                setCurrentPage(1);
                                setAppliedSearch(searchTerm.trim());
                            }
                        }}
                        sx={{ minWidth: { xs: '100%', sm: 320 }, flex: 1, maxWidth: 480 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<Search />}
                        onClick={() => {
                            setCurrentPage(1);
                            setAppliedSearch(searchTerm.trim());
                        }}
                    >
                        Buscar
                    </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Data</TableCell>
                                <TableCell>Título</TableCell>
                                <TableCell>Mensagem</TableCell>
                                <TableCell>Audiência</TableCell>
                                <TableCell>Link</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {historyLoading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : history.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Nenhuma notificação enviada ainda.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                history.map((item) => (
                                    <TableRow key={item.id} hover>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {formatWhen(item.created_at)}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, maxWidth: 220 }}>
                                            {item.title}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 320 }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {item.body}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={audienceLabel(item)}
                                                variant="outlined"
                                                sx={{ maxWidth: 260 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 180 }}>
                                            {item.link ? (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    title={item.link}
                                                >
                                                    {item.link}
                                                </Typography>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {totalPages > 1 && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <CustomPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default CreateNotificationPage;
